'use client';

import type { BaseInstance } from '@pigello/pigello-matrix';

import type { DynamicField } from '@/hooks/useForm/types';
import { usePerms } from '@/hooks/usePerms';
import { typeGuard } from '@/lib/utils';
import dynamic from 'next/dynamic';
import { useFormContext } from 'react-hook-form';
import type { SnakeCase } from 'type-fest';
import { AbstractObjectField } from './components/abstract-object-field';
import { AccountNumber } from './components/account-number-field';
import { AddressField } from './components/address-field';
import { Array } from './components/array-field';
import { Boolean } from './components/boolean-field';
import { ChoiceField } from './components/choice-field';
import ContentType from './components/content-type';
import { DateField } from './components/date-field';
import { DistributionInstruction } from './components/distribution-instruction-field';
import FloorField from './components/floor-field';
import { FMIField } from './components/fmi-field';
import { IBANAccount } from './components/iban-field';
import { DynamicFieldLoader } from './components/loaders';
import ManyRelationalField from './components/many-relation';
import { Markdown } from './components/markdown';
import { NumberField } from './components/number-field';
import { PriceField } from './components/price-field';
import RelationField from './components/relation-field';
import { TextField } from './components/text-field';
import { Textarea } from './components/textarea-field';
import { UniqueText } from './components/unique-text-field';
import { WeekField } from './components/week-field';
import type { FieldProps } from './types';
import { containsOrderedField } from './utils';

const FileField = dynamic(
  () => import('./components/file-field').then((mod) => mod.FileField),
  {
    ssr: false,
  }
);
/**
 * @description
 * This is a component that renders a form field.
 * @param name - Deprecated: Path name to the form field value. Only use formField.fields['nameOfYourField'] instead.
 * @param label - Label to display for the form field. Overrides the config label.
 * @param placeholder - Placeholder to display for the form field.
 * @param description - Description to display for the form field. Overrides the config description.
 * @param horizontal - Whether to display the form field horizontally. Vertical by default.
 * @param className - Class name to apply to the form field. Adds classNames to the ```<FormItem />``` component.
 * @param fieldType ```('string' | 'number')``` - Type of the form field. Defaults to 'string'. Only applies to ```<ChoiceField />```.
 * @example
 * ```tsx
 *  <DynamicField
      formField={form.fields['nameOfYourField']}
      name='name'
      label='Label'
      placeholder='Placeholder'
      description='A description'
      horizontal={true}
      className='my-class-name'
    />
 * ```
 */

const addressSpecialCases = [
  'includeApartmentIdInAddress',
  'includeCompanyAddress',
  'customPostalAddress',
];

export function DynamicField<Instance extends BaseInstance = BaseInstance>({
  name,
  formField,
  placeholder,
  label,
  description,
  horizontal = false,
  className,
  fieldType,
  fileFieldStyle = 'dropzone',
  fileFieldAccept,
  isLoading,
  disabled,
  btnLabel,
  filters,
  area,
  resize,
  rows,
  order,
  cols,
  overrideRequired,
  overrideNullable,
  overrideMaxLength,
  overrideRelationConfigModelName,
  choiceFieldSortingDisabled,
  transformValueFunction,
  validate,
  allowedDistributionMethods,
  allowInherit,
  allowedMinDate,
  allowedMaxDate,
  minNumber,
  maxNumber,
  integerOnly,
  enableGlobalFilters,
  onValueChange,
}: FieldProps<Instance> & {
  enableGlobalFilters?: boolean;
  onValueChange?: (value: unknown) => void;
}) {
  const { fields } = usePerms<Instance>(formField.config?.modelName);
  const formContext = useFormContext();

  const canHandleField =
    fields[formField.name]?.canHandle === true ||
    fields[formField.name]?.canHandle === undefined;

  const canView =
    fields[formField.name]?.canView === true ||
    fields[formField.name]?.canView === undefined;

  if (formField.readOnly || formField.name === 'tags' || !canView) {
    return null;
  }

  if (isLoading) {
    return (
      <DynamicFieldLoader
        {...{
          className,
          horizontal,
          type: formField.type,
        }}
      />
    );
  }

  if (
    formField.name.toString().toLowerCase().includes('address') &&
    !addressSpecialCases.includes(formField.name.toString())
  ) {
    const field = formField.config.fields[formField.name];

    if (!typeGuard(field, 'relationConfig')) {
      throw new Error('relationConfig is not in adress field');
    }

    return (
      <AddressField
        {...{
          formField,
          name: name ?? formField.name,
          placeholder,
          label,
          description,
          horizontal,
          className,
          disabled,
          canHandleField,
          modelName: field.relationConfig,
          onValueChange,
        }}
      />
    );
  }

  // In cases were we for some reason don't have ordered fields with the fmi
  // field, we fallback to the default field
  if (
    formField.name.toString().toLowerCase().includes('fmi') &&
    containsOrderedField<Instance>(formContext)
  ) {
    return (
      <FMIField
        {...{
          formField,
          name: name ?? formField.name,
          placeholder,
          label,
          description,
          horizontal,
          className,
          disabled,
          canHandleField,
          onValueChange,
          orderedFields: formContext.orderedFields,
        }}
      />
    );
  }

  switch (formField.type) {
    case 'array': {
      if (
        formField.name.toString().includes('floors') ||
        formField.name.toString().includes('excludeForAccounts')
      ) {
        return (
          <FloorField
            {...{
              formField,
              name: name ?? formField.name,
              placeholder,
              label,
              description,
              horizontal,
              className,
              disabled,
              canHandleField,
              onValueChange,
            }}
          />
        );
      }

      const field = formField.config.fields[formField.name];

      return (
        <Array
          {...{
            overrideRequired,
            formField,
            name: name ?? formField.name,
            placeholder,
            label,
            description,
            horizontal,
            className,
            disabled,
            canHandleField,
            onValueChange,
            choices: 'choices' in field ? field.choices : undefined,
          }}
        />
      );
    }

    case 'weektext': {
      return (
        <WeekField
          {...{
            formField,
            name: name ?? formField.name,
            placeholder,
            label,
            description,
            horizontal,
            className,
            disabled,
            canHandleField,
            onValueChange,
          }}
        />
      );
    }

    case 'text': {
      return (
        <TextField
          {...{
            formField,
            name: name ?? formField.name,
            placeholder,
            label,
            description,
            horizontal,
            className,
            isLoading,
            disabled,
            canHandleField,
            overrideMaxLength: overrideMaxLength,
            transformValueFunction,
            overrideRequired,
            onValueChange,
            validate,
          }}
        />
      );
    }

    case 'uniquetext': {
      return (
        <UniqueText
          {...{
            formField,
            name: name ?? formField.name,
            placeholder,
            label,
            description,
            horizontal,
            className,
            isLoading,
            disabled,
            canHandleField,
            onValueChange,
            modelName: formField.config.modelName,
            validateAgainst: formField.config.fields[formField.name]
              .externalFieldName as SnakeCase<keyof Instance>,
            validate,
          }}
        />
      );
    }

    case 'textarea': {
      return (
        <Textarea
          {...{
            formField,
            name: name ?? formField.name,
            placeholder,
            label,
            description,
            horizontal,
            className,
            disabled,
            canHandleField,
            resize,
            rows,
            cols,
            overrideRequired,
            onValueChange,
          }}
        />
      );
    }

    case 'pricefield': {
      return (
        <PriceField
          {...{
            area,
            formField,
            name: name ?? formField.name,
            placeholder,
            label,
            description,
            horizontal,
            className,
            isLoading,
            disabled,
            canHandleField,
            integerOnly,
            onValueChange,
          }}
        />
      );
    }

    case 'number': {
      return (
        <NumberField
          {...{
            formField,
            name: name ?? formField.name,
            placeholder,
            label,
            description,
            horizontal,
            className,
            isLoading,
            disabled,
            canHandleField,
            transformValueFunction,
            onValueChange,
            overrideRequired,
            minNumber,
            maxNumber,
          }}
        />
      );
    }
    case 'accountnumber': {
      return (
        <AccountNumber
          {...{
            formField,
            name: name ?? formField.name,
            placeholder,
            label,
            description,
            horizontal,
            className,
            isLoading,
            canHandleField,
            onValueChange,
          }}
        />
      );
    }
    case 'ibanfield': {
      return (
        <IBANAccount
          {...{
            formField,
            name: name ?? formField.name,
            placeholder,
            label,
            description,
            horizontal,
            className,
            isLoading,
            canHandleField,
            onValueChange,
          }}
        />
      );
    }

    case 'dateTime': {
      return (
        <DateField
          {...{
            granularity: 'minute',
            formField,
            name: name ?? formField.name,
            placeholder,
            label,
            description,
            horizontal,
            className,
            disabled,
            canHandleField,
            allowedMaxDate,
            allowedMinDate,
            overrideRequired,
            onValueChange,
          }}
        />
      );
    }
    case 'date': {
      return (
        <DateField
          {...{
            formField,
            name: name ?? formField.name,
            placeholder,
            label,
            description,
            horizontal,
            className,
            disabled,
            canHandleField,
            allowedMaxDate,
            allowedMinDate,
            overrideRequired,
            onValueChange,
          }}
        />
      );
    }

    case 'boolean': {
      return (
        <Boolean
          {...{
            formField,
            name: name ?? formField.name,
            label,
            description,
            placeholder,
            className,
            horizontal,
            isLoading,
            disabled,
            canHandleField,
            onValueChange,
          }}
        />
      );
    }

    case 'contentType': {
      return (
        <ContentType
          {...{
            formField,
            name: name ?? formField.name,
            label,
            description,
            placeholder,
            className,
            horizontal,
            isLoading,
            disabled,
            canHandleField,
            onValueChange,
          }}
        />
      );
    }

    case 'manyrelation': {
      const field = formField.config.fields[formField.name];

      if (!typeGuard(field, 'relationConfig')) {
        throw new Error('relationConfig is not in relationConfigName');
      }
      return (
        <ManyRelationalField
          {...{
            filters,
            order,
            formField,
            name: name ?? formField.name,
            label,
            description,
            placeholder,
            className,
            horizontal,
            disabled,
            canHandleField,
            enableGlobalFilters,
            modelName: field.relationConfig,
            onValueChange,
          }}
        />
      );
    }

    case 'relation': {
      const field = formField.config.fields[formField.name];

      if (!typeGuard(field, 'relationConfig')) {
        throw new Error('relationConfig is not in relation field');
      }

      return (
        <RelationField
          {...{
            overrideRequired,
            overrideNullable,
            overrideRelationConfigModelName,
            filters,
            order,
            formField,
            name: name ?? formField.name,
            label,
            description,
            placeholder,
            className,
            horizontal,
            isLoading,
            disabled,
            canHandleField,
            enableGlobalFilters,
            onValueChange,
            modelName: field.relationConfig,
            isNullable:
              formField?.config.fields?.[name]?.nullable != null
                ? formField.config.fields?.[name]?.nullable
                : true,
          }}
        />
      );
    }

    case 'choice': {
      const field = formField.config.fields[formField.name];

      if (!typeGuard(field, 'choices')) {
        throw new Error('choices is not in choices form field');
      }
      return (
        <ChoiceField
          {...{
            formField,
            name: name ?? formField.name,
            placeholder,
            label,
            className,
            description,
            horizontal,
            fieldType,
            isLoading,
            disabled,
            canHandleField,
            overrideRequired,
            choiceFieldSortingDisabled,
            choices: field.choices,
            onValueChange,
          }}
        />
      );
    }

    case 'fileField':
    case 'publicFileField': {
      return (
        <FileField
          {...{
            formField:
              formField as DynamicField<BaseInstance>[keyof BaseInstance],
            name: formField.name as keyof BaseInstance,
            placeholder,
            label,
            className,
            description,
            horizontal,
            fieldType,
            fileFieldStyle,
            disabled,
            canHandleField,
            fileFieldAccept,
          }}
        />
      );
    }

    case 'distributionInstruction': {
      return (
        <DistributionInstruction
          {...{
            formField,
            name: name ?? formField.name,
            placeholder,
            label,
            className,
            description,
            horizontal,
            fieldType,
            fileFieldStyle,
            disabled,
            canHandleField,
            allowedDistributionMethods,
            allowInherit,
            onValueChange,
          }}
        />
      );
    }

    case 'record': {
      return (
        <AbstractObjectField
          {...{
            formField,
            name: name ?? formField.name,
            placeholder,
            label,
            description,
            horizontal,
            className,
            disabled,
            canHandleField,
            btnLabel,
            onValueChange,
          }}
        />
      );
    }

    case 'markdown': {
      return (
        <Markdown
          {...{
            formField,
            name: name ?? formField.name,
            placeholder,
            label,
            description,
            className,
            disabled,
            overrideRequired,
            canHandleField,
            overrideMaxLength,
            onValueChange,
          }}
        />
      );
    }

    default: {
      console.log('Unknown input type: ', name);
      return (
        <>
          <div className='text-destructive'>
            Unknown input type: {formField.name.toString()}
          </div>
        </>
      );
    }
  }
}
